import { defineStore } from 'pinia';
import api from '@/api/api';
import type {ZNetwork, ZQueryResultNetworks} from '@schemas/network.schemas';
import type {Id, QueryResult} from "@typesDef/general.types";
import {setIdsUnique} from "@/stores/helpers/storeHelper";

export const useNetworksStore = defineStore('networks', {
    state: () => ({
        networksById: {} as Record<Id, ZNetwork>,
        myNetworkIds: [] as Id[],
        publicNetworkIds: [] as Id[],
        currentNetworkId: null as Id | null,
        // networks per event, used in the context of the event details page, to show the networks related to the event
        eventNetworksMeta: {} as Record<Id, QueryResult<Id>>
    }),
    getters: {
        currentNetwork(state): ZNetwork | null {
            return state.currentNetworkId ? state.networksById[state.currentNetworkId] ?? null : null;
        },
        myNetworks(state): ZNetwork[] {
            return state.myNetworkIds.map(id => state.networksById[id]).filter(Boolean) as ZNetwork[];
        },
        publicNetworks(state): ZNetwork[] {
            return state.publicNetworkIds.map(id => state.networksById[id]).filter(Boolean) as ZNetwork[];
        },
        eventNetworksForEvent: (state) => (eventId: Id): ZQueryResultNetworks => {
            const meta = state.eventNetworksMeta[eventId];
            if(!meta) {
                return { count: 0, results: [] };
            }
            return {
                count: meta.count,
                results: meta.results.map(id => state.networksById[id]).filter(Boolean) as ZNetwork[]
            };
        }

    },
    actions: {
        clearMyData() {
            this.networksById = {};
            this.myNetworkIds = [];
            this.publicNetworkIds = [];
            this.currentNetworkId = null;
        },
        // FIXME move them to general library since they are used in multiple stores
        upsertMany(docs: ZNetwork[]) {
            for (const e of docs) this.networksById[e._id] = e
        },
        /**
         * Fetches a network by its ID. The fetched network is added to the "networksById" map and the "currentNetworkId" is set to the fetched network's ID.
         */
        async fetchNetwork(networkId: Id): Promise<ZNetwork> {
            log.debug(`fetchNetwork(${networkId})`);
            const cached = this.networksById[networkId];
            if(cached) {
                this.currentNetworkId = networkId;
                return cached;
            }
            const doc = await api.getDocument<ZNetwork>('networks', networkId);
            this.networksById[doc._id] = doc;
            this.currentNetworkId = doc._id;
            // Note: We do not add the fetched network to the "my networks" or "public networks" lists since we don't know if
            //  it belongs to the user or if it's public. It will be added to the correct lists when we fetch the lists.
            return doc;
        },
        /**
         * Fetches the public networks. The fetched networks are added to the "public networks" list
         * but not to the "my networks" list since they might not be owned by the user.
         */
        async fetchNetworks(query: Record<string, any> = {}): Promise<ZNetwork[]> {
            const docs = await api.getDocuments<ZNetwork>(`networks`, query);
            this.upsertMany(docs);
            setIdsUnique(this.publicNetworkIds, docs.map(d => d._id));
            return docs;
        },
        /**
         * Fetches the networks owned by the current user. The fetched networks are added to the "my networks" list and also to the "public networks"
         * list since they are owned by the user and also public by default.
         */
        async fetchMyNetworks(query: Record<string, any> = {}): Promise<ZNetwork[]> {
            const docs = await api.getDocuments<ZNetwork>('networks/my', query);
            this.upsertMany(docs);
            setIdsUnique(this.myNetworkIds, docs.map(d => d._id));
            return docs;
        },

        async fetchNetworksByEventId(eventId: Id, query: Record<string, any> = {}): Promise<ZQueryResultNetworks> {
            const result = await api.getDocumentsWithCount<ZNetwork>('networks/event/' + eventId, query);
            this.upsertMany(result.results);
            this.eventNetworksMeta[eventId] = {
                results: result.results.map(d => d._id),
                count: result.count
            };
            return result;
        },

        /**
         * Adds a new network. The new network is added to both the "my networks" and "public networks" lists since it's owned by the user and also public by default.
         */
        async addNetwork(network: ZNetwork): Promise<ZNetwork> {
            const created = await api.postDocument<ZNetwork>('networks', network);
            if(!created || !created._id) {
                throw new Error('Failed to create network');
            }
            const newNetworkId = created._id;
            this.networksById [newNetworkId] = created;
            // add network to both lists (my and public) since it's mine and also public by default
            if(!this.myNetworkIds.includes(newNetworkId)) {
                this.myNetworkIds.unshift(newNetworkId); // we want to see our new network at the top of the list, so unshift instead of push
            }
            if(!this.publicNetworkIds.includes(newNetworkId)) {
                this.publicNetworkIds.unshift(newNetworkId);
            }
            this.currentNetworkId = newNetworkId;
            return created;
        },

        async updateNetwork(network: ZNetwork): Promise<ZNetwork> {
            const updated = await api.putDocument<ZNetwork>('networks', network);
            if(!updated || !updated._id) {
                throw new Error('Failed to update network');
            }
            this.networksById[updated._id] = updated;
            // no list synching needed since / it the same lists
            //   we do not allow changing the network's visibility (my/public) for now,
            // so the network will always stay in the same lists. If we allowed changing visibility, we would need to move the network between lists here.
            return updated;
        },
        async deleteNetwork(networkId: Id): Promise<void> {
            await api.deleteDocument<ZNetwork>('networks', networkId);
            // remove
            delete this.networksById[networkId];
            this.myNetworkIds = this.myNetworkIds.filter(id => id !== networkId);
            this.publicNetworkIds = this.publicNetworkIds.filter(id => id !== networkId);
            // if the deleted network was the current one, reset currentNetworkId
            if(this.currentNetworkId === networkId) {
                this.currentNetworkId = null;
            }
        }
    }
});
